import { useState } from 'react';
import { WIDGET_COLORS } from '../../constants/colors';
import {
  formatPoints,
  formatCurrency,
  formatCurrencyShort,
  progressPercent,
  downPaymentPercent,
  formatContactMonth,
} from '../../utils/formatSavingsPoints';
import type { SavingsPointsData } from '../../hooks/useGetSavingsPoints';
import { WidgetCardHeader } from '../widgetCardHeader';
import { WidgetDivider } from '../widgetDivider';
import { ProgressItem } from '../progressItem';
import { StatRow } from '../statRow';
import { TipRow } from '../tipRow';
import { InfoButton } from '../infoButton';
import { EligibilityInfoModal } from '../eligibilityInfoModal';

type Props = { data: SavingsPointsData };

export function BuildingPoints({ data }: Props) {
  const [infoOpen, setInfoOpen] = useState(false);

  const investmentNum = parseFloat(data.total_investment);
  const additionalNum = parseFloat(data.additional_downpayment_needed);
  const dpTarget = (investmentNum + additionalNum).toString();
  const pointsPercent = progressPercent(data.savings_points_earned, data.savings_points_required);
  const dpPercent = downPaymentPercent(data.total_investment, dpTarget);

  return (
    <>
      <WidgetCardHeader
        variant="default"
        badgeText="In Progress"
        description="Your savings are helping other families purchase their own homes while building towards purchasing your own."
      />

      <WidgetDivider />

      <ProgressItem
        label="Community Points"
        value={
          <span className="text-gray-500">
            <strong className="text-gray-800">{formatPoints(data.savings_points_earned)}</strong> of{' '}
            {formatPoints(data.savings_points_required)} required
          </span>
        }
        barPercent={pointsPercent}
        barColor={WIDGET_COLORS.dustBlue}
      />

      <ProgressItem
        label="Down Payment (GYS Home balance)"
        value={
          <span className="text-gray-500">
            <strong className="text-gray-800">{formatCurrency(data.total_investment)}</strong> of{' '}
            {formatCurrency(dpTarget)} target
          </span>
        }
        barPercent={dpPercent}
        barColor={WIDGET_COLORS.salmon}
      />

      <StatRow
        left={{
          value: `${formatPoints(data.savings_points_earned)} pts`,
          valueColor: WIDGET_COLORS.primary,
          label: 'Community Points',
        }}
        right={{
          value: data.contact_month ? formatContactMonth(data.contact_month) : '-',
          valueColor: WIDGET_COLORS.primary,
          label: (
            <span className="inline-flex items-center uppercase">
              Estimated Eligibility
              <InfoButton onClick={() => setInfoOpen(true)} />
            </span>
          ),
        }}
      />

      {/* TODO: Re-enable once top-up suggestion logic is finalised
      {(data.topup_suggestion_amount || parseFloat(data.additional_downpayment_needed) > 0) && (
        <TipRow
          left={
            data.topup_suggestion_amount && data.topup_contact_month && data.topup_months_faster
              ? {
                  label: `If you top up to ${formatCurrencyShort(data.topup_suggestion_amount)}`,
                  value: formatContactMonth(data.topup_contact_month),
                  sub: `${data.topup_months_faster} months faster`,
                }
              : {
                  label: 'Top-up suggestion',
                  value: '-',
                }
          }
          right={
            parseFloat(data.additional_downpayment_needed) > 0
              ? {
                  label: 'Down payment shortfall',
                  value: formatCurrency(data.additional_downpayment_needed),
                  sub:
                    data.deposit_percentage && data.property_value
                      ? `${formatCurrency(dpTarget)} = ${parseFloat(data.deposit_percentage).toFixed(0)}% of ${formatCurrency(data.property_value)} property`
                      : formatCurrency(dpTarget),
                }
              : undefined
          }
        />
      )}
      */}

      <EligibilityInfoModal open={infoOpen} onOpenChange={setInfoOpen} />
    </>
  );
}
